import { FC, ReactNode, createContext } from 'react';
import { types, Instance } from 'mobx-state-tree';
import { useLocalObservable } from 'mobx-react-lite';
import makeInspectable from 'mobx-devtools-mst';
import Cookies from 'js-cookie';
import { UserStore } from 'domain/user';
import { LDAPStore } from 'domain/ldap';
import { EndUserConfigStore } from 'domain/endUserConfig';
import { DashboardStore } from 'containers/Dashboard';
import { UpgradeLicenseStore } from 'containers/UpgradeLicense';
import { NotificationStore } from 'components/Notification';
import { AppPreloaderStore } from 'components/AppPreloader';
import { SmtpSettingsFormStore, AdminFormStore, EndUserSettingsFormStore } from 'components/Forms';
import { ImportStore } from 'containers/Import';
import {
  EmailTemplatesTableStore,
  InvitationStore,
  LogsTableStore,
  PaymentsTableStore,
  AdminsTableStore,
  UserEditRequestTableStore,
} from 'components/Tables';
import { InviteTableStore } from 'components/Tables/InviteTable/store';
import { ImportTableStore } from 'components/Tables/ImportTable/store';
import { appMenuStore } from 'components/AppMenu';
import { BaseTreeExplorerStore } from 'components/BaseTreeExplorer';
import { getToken } from 'utils';
import { ClientsTableStore } from 'components/Forms/ClientForm/store';
import { getOrganization } from 'utils/organizationUtils';
import { ClientProgressStore } from 'containers/ClientProgress';
import { GroupTableStore } from 'components/Tables/GroupTable/store';
import { DepartmentTableStore } from 'components/Tables/DepartmentTable/store';
import { UserTableStore } from 'components/Tables/UserTable/store';
import { DepartmentUserTableStore } from 'components/Tables/DepartmentUserTable/store';
import { AppStore } from 'components/Tables/AppsTable';
import { GoogleStore } from 'components/Tables/GoogleTable/store';
import { ZoomStore } from 'components/Tables/ZoomTable/store';
import { MicrosoftStore } from 'components/Tables/MicrosoftTable/store';
import { GithubStore } from 'components/Tables/GithubTable/store';
import { DevicesTableStore } from 'components/Tables/DevicesTable/store';
import { OdicStore } from 'components/Tables/OdicTable/store';
const { model } = types;

export const RootStore = model('RootStore', {
  userStore: UserStore,
  dashboardStore: DashboardStore,
  notificationStore: NotificationStore,
  appPreloaderStore: AppPreloaderStore,
  appMenuStore: appMenuStore,
  clientsTableStore: ClientsTableStore,
  userEditRequestTableStore: UserEditRequestTableStore,
  emailTemplatesTableStore: EmailTemplatesTableStore,
  invitationStore: InvitationStore,
  smtpSettingsFormStore: SmtpSettingsFormStore,
  logsTableStore: LogsTableStore,
  devicesTableStore: DevicesTableStore,
  odicStore: OdicStore,
  upgradeLicenseStore: UpgradeLicenseStore,
  paymentsTableStore: PaymentsTableStore,
  ldapStore: LDAPStore,
  importStore: ImportStore,
  adminsTableStore: AdminsTableStore,
  inviteTableStore: InviteTableStore,
  groupTableStore: GroupTableStore,
  userTableStore: UserTableStore,
  departmentUserTableStore: DepartmentUserTableStore,
  departmentTableStore: DepartmentTableStore,
  importTableStore: ImportTableStore,
  adminFormStore: AdminFormStore,
  baseTreeExplorerStore: BaseTreeExplorerStore,
  endUserSettingsFormStore: EndUserSettingsFormStore,
  endUserConfigStore: EndUserConfigStore,
  ClientProgressStore: ClientProgressStore,
  AppStore: AppStore,
  googleStore: GoogleStore,
  zoomStore: ZoomStore,
  MicrosoftStore: MicrosoftStore,
  GithubStore: GithubStore,
});

export type IRootStore = Instance<typeof RootStore>;

export const RootStoreContext = createContext<IRootStore | null>(null);

export const RootStoreProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const token = getToken();
  const organization = getOrganization() || Cookies.get('organization');
  const domain = Cookies.get('domain');

  // Clear invalid token on public pages
  const currentPath = window.location.pathname;
  const publicPaths = [
    '/login',
    '/register',
    '/admin/forgot-password',
    '/reset-password',
    '/user/forgot-password',
    '/user/reset-password',
    '/authenticate',
    '/2-fa',
    '/two-factor/recovery',
  ];
  const isPublicPage = publicPaths.some((path) => currentPath.includes(path)) || currentPath === '/';

  // Check if mobile for drawer initial state
  const isMobile = window.innerWidth <= 600;

  const store = useLocalObservable(() =>
    RootStore.create({
      userStore: {
        organizationName: isPublicPage ? '' : organization || '',
        domain: isPublicPage ? '' : domain || '',
        user: null,
        userProfileInfo: null,
        token: isPublicPage ? '' : token || '',
        newEndUserNotificationUpdate: false,
        loginStatusCall: false,
      },
      dashboardStore: {
        generalStatistic: null,
      },
      notificationStore: {
        type: 'success',
        isOpen: false,
        message: '',
        direction: 'default',
        upgradeLiscenceCheck: false,
      },
      appPreloaderStore: {
        show: false,
        type: 'outisde',
        size: 'big',
      },
      appMenuStore: {
        isOpen: !isMobile, // Desktop: open, Mobile: closed
      },
      clientsTableStore: {
        clients: [],
        selectedIds: [],
        clickedId: 0,
        loginLink: '',
        serviceStatuses: [],
        filter: '',
        groupFilter: [],
      },
      emailTemplatesTableStore: {
        emailTempaltes: [],
        emailtemp: [],
        filter: '',
        selectedId: 0,
      },
      AppStore: {
        googleProfileInfo: null,
      },
      invitationStore: {
        emailTempaltes: [],
        filter: '',
        selectedId: 0,
      },
      userEditRequestTableStore: {
        userData: [],
        userComparison: [],
        userFormDetail: [],
        filter: '',
        selectedId: 0,
        editselectArray: [],
      },
      smtpSettingsFormStore: {
        settings: [],
      },
      logsTableStore: {
        logs: [],
        filter: '',
        groupFilter: [],
        clickedId: 0,
        dateRangeFilter: {
          startDate: new Date(new Date().setHours(0, 0, 0, 0)),
          endDate: new Date(new Date().setHours(23, 59, 59, 999)),
        },
        logsInfo: null,
      },
      devicesTableStore: {
        logs: [],
        filter: '',
      },
      upgradeLicenseStore: {
        subscriptionPlans: [],
        lisenceInfo: null,
        step: 'upgrade-form',
        paymentMethod: 'card',
      },
      paymentsTableStore: {
        payments: [],
        filter: '',
        groupFilter: [],
        dateRangeFilter: {
          startDate: new Date(new Date().setHours(0, 0, 0, 0)),
          endDate: new Date(new Date().setHours(23, 59, 59, 999)),
        },
      },
      ldapStore: {
        credentials: null,
        users: [],
        departments: [],
        groups: [],
        currentBase: 'all',
        usersKeywordFilter: '',
        departmentsKeywordFilter: '',
        groupsKeywordFilter: '',
        FilterateRoute: {
          route: '',
          filterString: '',
        },
      },
      importStore: {
        ldapConnection: {
          host: '',
          domain: '',
          port: '',
          username: '',
          password: '',
          baseDn: '',
          active: '',
          ldapType: '',
          name: '',
          id: 0,
        },
        ldapAllConnections: [],
        ldapLastSync: {
          lastSyncTime: null,
          message: '',
          status: '',
        },
        mappingSettings: {
          people: [],
          group: [],
          department: [],
        },
        mapping: {
          people: [],
          group: [],
          department: [],
        },
        syncShedule: [],
        syncStatus: {
          numberOfRecordsSynchronizationCompleted: 0,
          numberOfRecordsSynchronizationInProgress: 0,
          totalNumberOfRecordsToSynchronize: 0,
          customerCloudLdapHostName: '',
          ezidCloudLdapHostName: '',
        },
        step: 'one',
      },
      adminsTableStore: {
        admins: [],
        selectedIds: [],
        clickedId: 0,
        selectedStatus: '',
        filter: '',
      },
      inviteTableStore: {
        invite: [],
        password: [],
        filter: '',
      },
      groupTableStore: {
        userGroup: [],
        filter: '',
      },
      userTableStore: {
        userGroup: [],
        filter: '',
      },
      departmentUserTableStore: {
        userDepartment: [],
        filter: '',
      },
      departmentTableStore: {
        userDepartment: [],
        filter: '',
      },
      importTableStore: {
        userLogs: [],
        group: [],
        department: [],
        filter: '',
      },
      adminFormStore: {
        roles: [],
      },
      baseTreeExplorerStore: {
        internalBase: '/',
        selectedBase: '/',
        selectedId: '',
        expandedIds: [],
        currentBaseNodeId: '',
        currentHistory: 'navigation',
        selectedinternalBaseGroup: true,
        inputHistory: {
          base: 'all',
          baseNodeId: '',
          parentNodeId: '',
          expandedNodeIds: [],
        },
        navigationHistory: {
          base: 'all',
          baseNodeId: '',
          parentNodeId: '',
          expandedNodeIds: [],
        },
      },
      endUserSettingsFormStore: {
        groupedUserNamePolicies: [],
        groupedPasswordPolicies: [],
        groupedGeneralSettings: [],
        endUserConfigSettings: null,
      },
      endUserConfigStore: {
        recoveryPermited: false,
        registartionPermited: false,
      },
      ClientProgressStore: {},
      googleStore: {
        syncusers: [],
        syncgroups: [],
        syncdepartments: [],
        usersKeywordFilter: '',
        syncToken: '',
      },
      zoomStore: {
        syncusers: [],
        syncgroups: [],
      },
      MicrosoftStore: {
        syncusers: [],
        syncgroups: [],
      },
      GithubStore: {
        syncGithubusers: [],
        syncGithubdepartment: [],
      },
      odicStore: {
        credentials: null,
        isLoading: false,
        error: null,
      },
    })
  );
  makeInspectable(store);
  return <RootStoreContext.Provider value={store}>{children}</RootStoreContext.Provider>;
};
